import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom } from 'jotai';
import { X } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

import { desiredColorSettingsAtom, settingsChangedAtom } from '../../atoms/settings';
import {
  DESIRED_COLOR_OPTIONS,
  type TYPE_DESIRED_COLOR_OPTIONS,
} from '../../constants/style-settings';
import { getColorFromName } from '../../utils/getColorFromName';

const ColorItem = ({
  active,
  color,
  label,
  onClickItem,
}: {
  active: boolean;
  color: TYPE_DESIRED_COLOR_OPTIONS;
  label: string;
  onClickItem: (option: TYPE_DESIRED_COLOR_OPTIONS) => void;
}) => {
  return (
    <button
      type="button"
      className={cn(
        'flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1',
        active ? 'bg-[#07B1BE1F]' : 'bg-[#0000001F]'
      )}
      onClick={() => onClickItem(color)}
    >
      <div
        className="h-[13px] w-[13px] rounded-full"
        style={{ backgroundColor: getColorFromName(color) }}
      />
      <span className="text-[10px] font-medium leading-none">{label}</span>
      {active ? <X className="h-[10px] w-[10px]" /> : null}
    </button>
  );
};

export const DesiredColorSettings = () => {
  const [colors, setColors] = useAtom(desiredColorSettingsAtom);
  const [, setSettingsChanged] = useAtom(settingsChangedAtom);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const desiredColorOptions = Object.values(DESIRED_COLOR_OPTIONS);

  const handleSelectColor = (option: TYPE_DESIRED_COLOR_OPTIONS) => {
    setColors((prev) => {
      if (prev.includes(option)) {
        return prev.filter((color) => color !== option);
      }

      return [...prev, option];
    });
    setSettingsChanged(true);
  };

  return (
    <Accordion
      type="single"
      className="w-full overflow-hidden rounded-sm bg-[#EFF4F5]"
      onValueChange={(val: string) => setIsOpen(!!val)}
      collapsible
    >
      <AccordionItem value="desired-color-settings">
        <AccordionTrigger className="flex min-h-[40px] w-full cursor-pointer items-center rounded-b-sm px-4 py-2 sm:min-h-[26px] sm:px-2 sm:py-1 lg:min-h-[40px] lg:px-4 lg:py-2">
          <span className="flex-1 text-left text-xs font-semibold lg:text-sm">
            {t('desiredColor')}
          </span>
        </AccordionTrigger>
        {!isOpen && colors.length > 0 ? (
          <div className="flex flex-wrap gap-1 px-4 pb-3">
            {colors.map((color) => (
              <ColorItem
                key={color}
                color={color}
                label={t(color)}
                onClickItem={handleSelectColor}
                active
              />
            ))}
          </div>
        ) : null}
        <AccordionContent className="px-4 pb-3">
          <div className="flex flex-wrap gap-1">
            {desiredColorOptions.map((color) => (
              <ColorItem
                key={color}
                active={colors.includes(color)}
                color={color}
                label={t(color)}
                onClickItem={handleSelectColor}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
